import React from "react";
import { SplitSquareHorizontal, AlertTriangle, ArrowRight, CheckCircle2 } from "lucide-react";
import { PersonBadge } from "@/components/PersonBadge";
import { idr, money, computeInitialsMap, firstName, itemUnits, portionFor, totalAssigned, itemPortions, isFullyAssigned, effectiveUnits, fmtQty } from "@/lib/format";

export default function AssignSection({ participants, items, currency = "IDR", onAddUnit, onRemoveUnit, onSplitEvenly, summary, onContinue, canContinue }) {
  const unassignedCount = items.filter((it) => !isFullyAssigned(it)).length;
  const imap = computeInitialsMap(participants);
  const m = (v) => money(v, currency);

  if (participants.length === 0) {
    return (
      <div data-testid="assign-section" className="gz-rise text-center py-10 text-[var(--gz-muted)] text-sm border border-dashed border-[var(--gz-line)] rounded-xl">
        Add people first, then come back to assign items.
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div data-testid="assign-section" className="gz-rise text-center py-10 text-[var(--gz-muted)] text-sm border border-dashed border-[var(--gz-line)] rounded-xl">
        No items yet. Scan a receipt or add items first.
      </div>
    );
  }

  return (
    <div data-testid="assign-section" className="gz-rise">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-[15px] font-semibold">Assign each item</h2>
        <span className="text-xs text-[var(--gz-muted)]">Tap = 1 unit, share via Split evenly</span>
      </div>

      {unassignedCount > 0 && (
        <div data-testid="unassigned-warning" className="flex items-center gap-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 px-4 py-3 text-sm mb-4">
          <AlertTriangle size={18} />
          {unassignedCount} item{unassignedCount > 1 ? "s" : ""} not fully assigned yet.
        </div>
      )}

      <div className="space-y-3">
        {items.map((it) => {
          const units = itemUnits(it);
          const qty = Number(it.qty) || 0;
          const portions = itemPortions(it);
          const taken = totalAssigned(it);
          const remaining = Math.max(0, qty - taken);
          const full = remaining <= 1e-6 && taken > 1e-6;
          const distinct = Object.keys(portions).length;
          const shared = full && (taken > qty + 1e-6 || Object.values(portions).some((v) => Math.abs(v - Math.round(v)) > 1e-6));
          const unitPrice = Number(it.unit_price) || 0;
          return (
            <div key={it.id} data-testid={`assign-item-${it.id}`} className="rounded-xl border border-[var(--gz-line)] p-3.5">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium truncate">{it.name || "Item"}</div>
                  <div className="text-xs text-[var(--gz-muted)]">
                    {units} × {m(unitPrice)}
                  </div>
                </div>
                <div className="font-semibold text-sm tabular-nums shrink-0">{m(qty * unitPrice)}</div>
              </div>

              <div className="flex items-start gap-3 mt-3 flex-wrap">
                {participants.map((p) => {
                  const c = portionFor(it, p.id);
                  const eff = effectiveUnits(it, p.id);
                  return (
                    <div key={p.id} className="flex flex-col items-center gap-1 w-12">
                      <PersonBadge
                        person={p}
                        size={40}
                        label={imap[p.id]}
                        count={c > 0 ? fmtQty(eff) : 0}
                        selected={c > 0}
                        onClick={() => onAddUnit(it.id, p.id)}
                        onCountClick={() => onRemoveUnit(it.id, p.id)}
                        testId={`assign-${it.id}-${p.id}`}
                        countTestId={`assign-remove-${it.id}-${p.id}`}
                      />
                      <span className={`text-[10px] leading-tight text-center w-full truncate ${c > 0 ? "text-[var(--gz-ink)] font-medium" : "text-[var(--gz-muted)]"}`}>
                        {firstName(p.name)}
                      </span>
                    </div>
                  );
                })}
                {participants.length > 1 && (
                  <button
                    data-testid={`split-evenly-${it.id}`}
                    onClick={() => onSplitEvenly(it.id)}
                    className="h-10 px-3 rounded-full border border-[var(--gz-line)] text-xs font-medium text-[var(--gz-muted)] flex items-center gap-1 active:scale-95 transition-transform"
                  >
                    <SplitSquareHorizontal size={14} /> Split evenly
                  </button>
                )}
              </div>

              <div className="mt-3 text-xs">
                {remaining > 1e-6 ? (
                  <span data-testid={`item-status-${it.id}`} className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium">
                    <AlertTriangle size={14} /> {fmtQty(remaining)} of {units} unit{units > 1 ? "s" : ""} unassigned
                  </span>
                ) : shared ? (
                  <span data-testid={`item-status-${it.id}`} className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                    <CheckCircle2 size={14} /> Shared evenly between {distinct} {distinct > 1 ? "people" : "person"}
                  </span>
                ) : full ? (
                  <span data-testid={`item-status-${it.id}`} className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                    <CheckCircle2 size={14} /> All {units} unit{units > 1 ? "s" : ""} assigned
                  </span>
                ) : (
                  <span data-testid={`item-status-${it.id}`} className="inline-flex items-center gap-1 text-[var(--gz-muted)]">
                    Tap a person to assign
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <button
        data-testid="continue-to-summary-btn"
        onClick={onContinue}
        aria-disabled={!canContinue}
        className={`mt-5 w-full py-3.5 rounded-full font-semibold flex items-center justify-center gap-2 transition-transform ${
          canContinue
            ? "bg-[var(--gz-primary)] text-white active:scale-[0.98]"
            : "bg-[var(--gz-line)] text-[var(--gz-muted)] cursor-not-allowed"
        }`}
      >
        {canContinue ? (
          <>View the breakdown <ArrowRight size={18} /></>
        ) : (
          "Assign every unit to continue"
        )}
      </button>
    </div>
  );
}
